import { httpsCallable } from 'firebase/functions';
import {
  collection, doc, getDocs, onSnapshot, orderBy, query, limit,
} from 'firebase/firestore';
import { db, functions } from '../firebase.js';

// ─────────────────────────────────────────────────────────────────────────
// Wallet — client library
//
// Reading the balance and ledger is a plain owner-scoped Firestore read
// (wallets/{uid} is readable only by its owner, never writable — see
// firestore.rules). Money only ever moves through Cloud Functions
// (functions/wallet.js): recharge (real Razorpay Checkout, see below),
// purchase debits, sale credits, admin adjustments.
//
// All amounts are INTEGER PAISE server-side; formatPaise() is the one
// place they become ₹ strings for display.
// ─────────────────────────────────────────────────────────────────────────

const createRechargeOrderFn = httpsCallable(functions, 'createRechargeOrder');
const verifyRechargeFn = httpsCallable(functions, 'verifyRecharge');
const createSchoolRechargeOrderFn = httpsCallable(functions, 'createSchoolRechargeOrder');
const verifySchoolRechargeFn = httpsCallable(functions, 'verifySchoolRecharge');
const requestSellerRecoveryFn = httpsCallable(functions, 'requestSellerRecovery');
const initializeAccountFn = httpsCallable(functions, 'initializeAccount');
const ensureViewingAccessFn = httpsCallable(functions, 'ensureViewingAccess');

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let razorpayScriptPromise = null;

// Loaded lazily — only when a recharge is actually attempted — rather than
// on every page load, since most sessions never touch the wallet page.
function loadRazorpayCheckout() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_CHECKOUT_SRC;
    script.onload = () => resolve();
    script.onerror = () => {
      razorpayScriptPromise = null; // allow a retry on the next attempt
      reject(new Error('Could not load the payment gateway. Check your connection and try again.'));
    };
    document.head.appendChild(script);
  });
  return razorpayScriptPromise;
}

export function formatPaise(paise) {
  const rupees = (Number(paise) || 0) / 100;
  return `₹${rupees.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function watchWallet(uid, callback) {
  return onSnapshot(doc(db, 'wallets', uid), (snap) => {
    callback(snap.exists()
      ? {
        balancePaise: snap.data().balancePaise || 0,
        recoverableSalesPaise: snap.data().recoverableSalesPaise || 0,
      }
      : { balancePaise: 0, recoverableSalesPaise: 0 });
  });
}

// School invoices — read-only receipts issued automatically when a rostered
// student signs in and their school has set a per-student price (see
// functions/schools.js maybeIssueStudentInvoice). Owner-scoped Firestore
// read; only Cloud Functions can ever write one.
export async function fetchMyInvoices(uid, max = 50) {
  const snap = await getDocs(query(
    collection(db, 'users', uid, 'invoices'),
    orderBy('issuedAt', 'desc'),
    limit(max),
  ));
  return snap.docs.map((d) => {
    const v = d.data();
    return {
      id: d.id,
      invoiceNumber: v.invoiceNumber || d.id,
      schoolCode: v.schoolCode || '',
      schoolName: v.schoolName || '',
      studentEmail: v.studentEmail || '',
      totalPaise: v.totalPaise || 0,
      platformPaise: v.platformPaise || 0,
      instructorPaise: v.instructorPaise || 0,
      currency: v.currency || 'INR',
      taxNote: v.taxNote || 'All amounts are inclusive of all applicable taxes.',
      issuedAtMs: v.issuedAt?.toMillis?.() || null,
      termEndsAtMs: v.termEndsAt?.toMillis?.() || null,
    };
  });
}

export function watchWalletTransactions(uid, callback, max = 100) {
  const q = query(
    collection(db, 'wallets', uid, 'transactions'),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(q, (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
}

/**
 * rechargeWallet — real Razorpay Checkout flow (test-mode keys until the
 * project goes live; nothing about this code changes when it does). Three
 * steps, matching functions/wallet.js:
 *   1. createRechargeOrder — server creates a real Razorpay Order for the
 *      exact rupee amount and hands back {orderId, keyId, amountPaise}.
 *   2. Razorpay's own hosted Checkout UI opens and collects payment
 *      details — this app never sees card/UPI details.
 *   3. On success, verifyRecharge checks the signature Razorpay returned
 *      before the wallet is actually credited — this resolves once that's
 *      done, with the confirmed new balance.
 * Rejects with a user-facing Error on cancel, failure, or a gateway/
 * network problem — same "show err.message" pattern the caller (src/views/
 * wallet.js) already used for the old dummy gateway.
 */
export async function rechargeWallet(amountRupees) {
  const { data: order } = await createRechargeOrderFn({ amountRupees });
  await loadRazorpayCheckout();

  return new Promise((resolve, reject) => {
    const checkout = new window.Razorpay({
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amountPaise,
      currency: order.currency,
      name: 'Learning Lab',
      description: 'Wallet recharge',
      theme: { color: '#6c5ce7' },
      handler: async (response) => {
        try {
          const { data } = await verifyRechargeFn({
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
          resolve(data); // { ok, balancePaise, paymentId }
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled.')),
      },
    });
    checkout.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed. Please try again.'));
    });
    checkout.open();
  });
}

/**
 * rechargeSchoolWallet — self-serve top-up of a SCHOOL's prepaid wallet via
 * the same Razorpay Checkout flow as rechargeWallet above, but the credit
 * lands on the caller's school (server resolves it from ownerUid — the client
 * never names the school). Only a school owner may call it. Resolves with
 * { ok, balancePaise, paymentId }; rejects with a user-facing Error on
 * cancel/failure.
 */
export async function rechargeSchoolWallet(amountRupees) {
  const { data: order } = await createSchoolRechargeOrderFn({ amountRupees });
  await loadRazorpayCheckout();

  return new Promise((resolve, reject) => {
    const checkout = new window.Razorpay({
      key: order.keyId,
      order_id: order.orderId,
      amount: order.amountPaise,
      currency: order.currency,
      name: 'Simustage',
      description: 'School wallet top-up',
      theme: { color: '#6c5ce7' },
      handler: async (response) => {
        try {
          const { data } = await verifySchoolRechargeFn({
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            signature: response.razorpay_signature,
          });
          resolve(data);
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled.')),
      },
    });
    checkout.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed. Please try again.'));
    });
    checkout.open();
  });
}

export async function requestSellerRecovery(payload) {
  const { data } = await requestSellerRecoveryFn({ ...payload, idempotencyKey: crypto.randomUUID() });
  return data;
}

// Called once per sign-in (see src/lib/auth.js) — a no-op after the first
// time for a given account (server-side idempotent), so it's safe to call
// on every login without ever resetting an already-granted trial.
export async function initializeAccount() {
  const { data } = await initializeAccountFn();
  return data;
}

// Called by src/lib/viewingAccess.js right before showing gated content —
// see functions/wallet.js ensureViewingAccess for the full billing logic.
// Returns { blocked, costPaise, balancePaise, viewingStatus,
// viewingPeriodEndMs, trialEndsAtMs }.
export async function ensureViewingAccess(opts) {
  const { data } = await ensureViewingAccessFn(opts || {});
  return data;
}

export const TXN_TYPE_LABELS = {
  recharge: { label: 'Recharge', emoji: '💳' },
  purchase: { label: 'Purchase', emoji: '🛒' },
  sale_credit: { label: 'Sale earning', emoji: '💰' },
  admin_adjustment: { label: 'Adjustment', emoji: '🛠️' },
  generation_charge: { label: 'Content generation', emoji: '✨' },
  generation_refund: { label: 'Generation refund', emoji: '↩️' },
  dynamic_content_charge: { label: 'Dynamic content (Ask AI)', emoji: '🧩' },
  dynamic_content_refund: { label: 'Ask AI refund', emoji: '↩️' },
  viewing_subscription_charge: { label: 'Monthly viewing access', emoji: '📺' },
};
